import { useState, useEffect, useCallback, type FormEvent } from 'react';
import {
    fetchSchools,
    fetchSchool,
    createSchool,
    updateSchool,
    deleteSchool,
    fetchStaffOptions,
    fetchBoards,
    mapSchoolToUi,
    type BoardApiRecord,
    type FormBoardEntry,
    type SchoolApiRecord,
    type SchoolUi,
    type StaffOption,
} from '../../services/schoolApi';
import type { ApiError } from '../../services/api';
import './SchoolManagement.css';
import { useTranslation } from 'react-i18next';

/* ── Helpers ── */
function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/* ── Component ── */

interface FormErrors {
    name?: string;
    school_code?: string;
    boards?: string;
}

type DrawerMode = null | 'create' | 'edit' | 'view';

export default function SchoolManagement() {
    const { t: _t } = useTranslation();
    /* ---- data state ---- */
    const [schools, setSchools] = useState<SchoolUi[]>([]);
    const [boards, setBoards] = useState<BoardApiRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    /* ---- search ---- */
    const [search, setSearch] = useState('');

    /* ---- drawer state ---- */
    const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
    const [editingId, setEditingId] = useState<string | null>(null);

    /* ---- form state ---- */
    const [formName, setFormName] = useState('');
    const [formCode, setFormCode] = useState('');
    const [formEstYear, setFormEstYear] = useState('');
    const [formPrincipalId, setFormPrincipalId] = useState<string | null>(null);
    const [formEmail, setFormEmail] = useState('');
    const [formPhone, setFormPhone] = useState('');
    const [formWebsite, setFormWebsite] = useState('');
    const [formAddress, setFormAddress] = useState('');
    const [formCity, setFormCity] = useState('');
    const [formState, setFormState] = useState('');
    const [formPostal, setFormPostal] = useState('');
    const [formCountry, setFormCountry] = useState('');
    const [formIsActive, setFormIsActive] = useState(true);
    const [formBoards, setFormBoards] = useState<FormBoardEntry[]>([]);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    /* ---- code uniqueness ---- */
    const [codeStatus, setCodeStatus] = useState<'idle' | 'loading' | 'available' | 'taken'>('idle');

    /* ---- staff options for principal select ---- */
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [staffLoading, setStaffLoading] = useState(false);

    /* ---- delete confirm ---- */
    const [deleteTarget, setDeleteTarget] = useState<SchoolUi | null>(null);

    /* ---- timestamps for editing ---- */
    const [editCreatedAt, setEditCreatedAt] = useState('');
    const [editUpdatedAt, setEditUpdatedAt] = useState('');

    /* ---- view panel ---- */
    const [viewSchool, setViewSchool] = useState<SchoolApiRecord | null>(null);
    const [viewLoading, setViewLoading] = useState(false);

    /* ============== Load data ============== */
    const loadSchools = useCallback(async () => {
        setLoading(true);
        setErrorMsg('');
        try {
            const [schoolsRes, boardsRes] = await Promise.all([
                fetchSchools(),
                fetchBoards(),
            ]);
            setSchools(schoolsRes.data.map(mapSchoolToUi));
            setBoards(boardsRes);
        } catch (err) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to load schools.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSchools();
    }, [loadSchools]);

    /* ============== Debounced code uniqueness check ============== */
    // useEffect(() => {
    //     if (!formCode.trim()) {
    //         setCodeStatus('idle');
    //         return;
    //     }
    //     setCodeStatus('loading');
    //     const timer = setTimeout(async () => {
    //         const available = await checkSchoolCodeAvailability(formCode.trim(), editingId ?? undefined);
    //         setCodeStatus(available ? 'available' : 'taken');
    //     }, 400);
    //     return () => clearTimeout(timer);
    // }, [formCode, editingId]);

    /* ============== Load staff options when drawer opens ============== */
    useEffect(() => {
        if (!drawerMode) return;
        let cancelled = false;
        setStaffLoading(true);
        fetchStaffOptions()
            .then((opts) => {
                if (!cancelled) setStaffOptions(opts);
            })
            .catch(() => {
                if (!cancelled) setStaffOptions([]);
            })
            .finally(() => {
                if (!cancelled) setStaffLoading(false);
            });
        return () => { cancelled = true; };
    }, [drawerMode]);

    /* ============== Filtered list ============== */
    const filtered = schools.filter((s) => {
        const q = search.toLowerCase();
        return (
            s.name.toLowerCase().includes(q) ||
            s.school_code.toLowerCase().includes(q) ||
            (s.city && s.city.toLowerCase().includes(q)) ||
            (s.principal_name && s.principal_name.toLowerCase().includes(q)) ||
            (s.contact_email && s.contact_email.toLowerCase().includes(q)) ||
            (s.contact_phone && s.contact_phone.toLowerCase().includes(q))
        );
    });

    /* ============== Drawer helpers ============== */
    const resetForm = useCallback(() => {
        setFormName('');
        setFormCode('');
        setFormEstYear('');
        setFormPrincipalId(null);
        setFormEmail('');
        setFormPhone('');
        setFormWebsite('');
        setFormAddress('');
        setFormCity('');
        setFormState('');
        setFormPostal('');
        setFormCountry('');
        setFormIsActive(true);
        setFormBoards([]);
        setErrors({});
        setCodeStatus('idle');
        setEditingId(null);
        setEditCreatedAt('');
        setEditUpdatedAt('');
    }, []);

    const openCreate = () => {
        resetForm();
        setDrawerMode('create');
    };

    const populateBoardsFromDetail = (detail: SchoolApiRecord) => {
        if (detail.boards && detail.boards.length > 0) {
            setFormBoards(
                detail.boards.map((b) => ({
                    board_id: b.id,
                    affiliation_number: b.affiliation?.affiliation_number ?? '',
                    affiliated_since: b.affiliation?.affiliated_since ?? '',
                    expires_at: b.affiliation?.expires_at ?? '',
                }))
            );
        }
    };

    const openEdit = async (school: SchoolUi, preloadedDetail?: SchoolApiRecord) => {
        resetForm();
        setFormName(school.name);
        setFormCode(school.school_code);
        setFormEstYear(school.established_year ? String(school.established_year) : '');
        setFormPrincipalId(school.principal_user_id);
        setFormEmail(school.contact_email || '');
        setFormPhone(school.contact_phone || '');
        setFormWebsite(school.website || '');
        setFormAddress(school.address || '');
        setFormCity(school.city || '');
        setFormState(school.state || '');
        setFormPostal(school.postal_code || '');
        setFormCountry(school.country || '');
        setFormIsActive(school.is_active);
        setEditingId(school.id);
        setEditCreatedAt(school.created_at);
        setEditUpdatedAt(school.updated_at);
        setDrawerMode('edit');

        if (preloadedDetail) {
            populateBoardsFromDetail(preloadedDetail);
        } else {
            try {
                const detail = await fetchSchool(school.id);
                populateBoardsFromDetail(detail);
            } catch {
                // If fetching details fails, boards stay empty
            }
        }
    };

    const openView = async (schoolId: string) => {
        setDrawerMode('view');
        setViewLoading(true);
        setViewSchool(null);
        try {
            const detail = await fetchSchool(schoolId);
            setViewSchool(detail);
        } catch (err) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to load school details.');
            setTimeout(() => setErrorMsg(''), 5000);
            setDrawerMode(null);
        } finally {
            setViewLoading(false);
        }
    };

    const closeDrawer = () => {
        setDrawerMode(null);
        resetForm();
        setViewSchool(null);
    };

    /* ============== Validation ============== */
    const validate = (): boolean => {
        const e: FormErrors = {};
        if (!formName.trim()) e.name = 'School name is required.';
        if (!formCode.trim()) e.school_code = 'School code is required.';
        else if (codeStatus === 'taken')
            e.school_code = 'This school code is already in use within your organization.';

        // Check board date validation
        const hasInvalidBoardDates = formBoards.some(
            (b) => b.expires_at && b.affiliated_since && b.expires_at <= b.affiliated_since
        );
        if (hasInvalidBoardDates) e.boards = 'Expiry date must be after affiliated since date for all boards.';

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /* ============== Submit ============== */
    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);
        setErrorMsg('');

        const payload: Record<string, unknown> = {
            name: formName.trim(),
            school_code: formCode.trim(),
            principal_user_id: formPrincipalId,
            established_year: formEstYear ? Number(formEstYear) : null,
            contact_email: formEmail || null,
            contact_phone: formPhone || null,
            website: formWebsite || null,
            address: formAddress || null,
            city: formCity || null,
            state: formState || null,
            postal_code: formPostal || null,
            country: formCountry || null,
            is_active: formIsActive,
            metadata: {},
            boards: formBoards.map((b) => ({
                board_id: b.board_id,
                affiliation_number: b.affiliation_number || null,
                affiliated_since: b.affiliated_since || null,
                expires_at: b.expires_at || null,
            })),
        };

        try {
            if (drawerMode === 'edit' && editingId) {
                await updateSchool(editingId, payload);
                setSuccessMsg('School updated successfully!');
            } else {
                await createSchool(payload);
                setSuccessMsg('School created successfully!');
            }

            closeDrawer();
            await loadSchools();
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const formErrors: FormErrors = {};
                if (apiErr.errors.name) formErrors.name = apiErr.errors.name[0];
                if (apiErr.errors.school_code) formErrors.school_code = apiErr.errors.school_code[0];
                setErrors(formErrors);
            }
            setErrorMsg(apiErr.message || 'Failed to save school.');
            setTimeout(() => setErrorMsg(''), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ============== Delete ============== */
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteSchool(deleteTarget.id);
            setDeleteTarget(null);
            setSuccessMsg('School removed successfully!');
            await loadSchools();
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            const apiErr = err as ApiError;
            setDeleteTarget(null);
            setErrorMsg(apiErr.message || 'Failed to delete school.');
            setTimeout(() => setErrorMsg(''), 5000);
        }
    };

    /* ============== Board toggle ============== */
    const toggleBoard = (id: number) => {
        setFormBoards((prev) =>
            prev.some((b) => b.board_id === id)
                ? prev.filter((b) => b.board_id !== id)
                : [...prev, { board_id: id, affiliation_number: '', affiliated_since: '', expires_at: '' }]
        );
    };

    const updateBoardField = (boardId: number, field: keyof Omit<FormBoardEntry, 'board_id'>, value: string) => {
        setFormBoards((prev) =>
            prev.map((b) => (b.board_id === boardId ? { ...b, [field]: value } : b))
        );
    };

    /* ============== Principal selection ============== */
    const handlePrincipalChange = (userId: string) => {
        setFormPrincipalId(userId || null);
    };

    /* ================================================================= */
    /*  R E N D E R                                                       */
    /* ================================================================= */
    return (
        <div className="schools">
            {/* Success toast */}
            {successMsg && (
                <div className="schools__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span>{successMsg}</span>
                    <button className="schools__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Error toast */}
            {errorMsg && (
                <div className="schools__toast schools__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <span>{errorMsg}</span>
                    <button className="schools__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Header */}
            <div className="schools__header">
                <div className="schools__header-text">
                    <h1 className="schools__title">Schools</h1>
                    <p className="schools__subtitle">Manage schools within your organization.</p>
                </div>
                <div className="schools__header-actions">
                    <div className="schools__search-wrap">
                        <svg className="schools__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            className="schools__search"
                            type="text"
                            placeholder="Search schools…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <button className="schools__add-btn" onClick={openCreate}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add School
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="schools__table-wrap">
                {loading ? (
                    <div className="schools__loading">
                        <div className="schools__loader">
                            <span className="schools__loader-dot" />
                            <span className="schools__loader-dot" />
                            <span className="schools__loader-dot" />
                        </div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="schools__empty">
                        <div className="schools__empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <path d="M9 21V9h6v12" />
                                <path d="M3 9h18" />
                            </svg>
                        </div>
                        <h3 className="schools__empty-title">
                            {search ? 'No schools match your search' : 'No schools yet'}
                        </h3>
                        <p className="schools__empty-text">
                            {search
                                ? 'Try adjusting your search terms.'
                                : 'Add your first school to get started.'}
                        </p>
                    </div>
                ) : (
                    <table className="schools__table">
                        <thead>
                            <tr>
                                <th className="schools__th">Name</th>
                                <th className="schools__th">Code</th>
                                <th className="schools__th">Principal</th>
                                <th className="schools__th">City</th>
                                <th className="schools__th">Email</th>
                                <th className="schools__th">Phone</th>
                                <th className="schools__th">Status</th>
                                <th className="schools__th schools__th--actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((s) => (
                                <tr className="schools__table-row" key={s.id}>
                                    <td className="schools__td schools__td--name">
                                        <button
                                            className="schools__name-link"
                                            type="button"
                                            onClick={() => openView(s.id)}
                                        >
                                            {s.name}
                                        </button>
                                    </td>
                                    <td className="schools__td schools__td--code">{s.school_code}</td>
                                    <td className="schools__td">
                                        {s.principal_name || (
                                            <span className="schools__muted">Unassigned</span>
                                        )}
                                    </td>
                                    <td className="schools__td">
                                        {s.city || <span className="schools__muted">—</span>}
                                    </td>
                                    <td className="schools__td">
                                        {s.contact_email || <span className="schools__muted">—</span>}
                                    </td>
                                    <td className="schools__td">
                                        {s.contact_phone || <span className="schools__muted">—</span>}
                                    </td>
                                    <td className="schools__td">
                                        <span
                                            className={`schools__badge ${s.is_active
                                                ? 'schools__badge--active'
                                                : 'schools__badge--inactive'
                                                }`}
                                        >
                                            {s.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="schools__td schools__td--actions">
                                        <button
                                            className="schools__row-btn"
                                            onClick={() => openEdit(s)}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                            Edit
                                        </button>
                                        <button
                                            className="schools__row-btn schools__row-btn--delete"
                                            onClick={() => setDeleteTarget(s)}
                                            style={{ marginLeft: 6 }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6" />
                                                <path d="M14 11v6" />
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ============= View Panel ============= */}
            {drawerMode === 'view' && (
                <>
                    <div className="schools__overlay" onClick={closeDrawer} />
                    <aside className="schools__drawer">
                        <div className="schools__drawer-header">
                            <h2 className="schools__drawer-title">School Details</h2>
                            <button className="schools__drawer-close" onClick={closeDrawer} title="Close">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        {viewLoading ? (
                            <div className="schools__drawer-body">
                                <div className="schools__loading">
                                    <div className="schools__loader">
                                        <span className="schools__loader-dot" />
                                        <span className="schools__loader-dot" />
                                        <span className="schools__loader-dot" />
                                    </div>
                                </div>
                            </div>
                        ) : viewSchool ? (
                            <div className="schools__drawer-body">
                                {/* Basic Information */}
                                <div className="schools__card">
                                    <h3 className="schools__card-title">Basic Information</h3>
                                    <div className="schools__view-field">
                                        <span className="schools__view-label">School Name</span>
                                        <span className="schools__view-value">{viewSchool.name}</span>
                                    </div>
                                    <div className="schools__row">
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">School Code</span>
                                            <span className="schools__view-value schools__view-value--code">{viewSchool.school_code}</span>
                                        </div>
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">Established Year</span>
                                            <span className="schools__view-value">{viewSchool.established_year ?? <span className="schools__muted">—</span>}</span>
                                        </div>
                                    </div>
                                    <div className="schools__view-field">
                                        <span className="schools__view-label">Status</span>
                                        <span>
                                            <span className={`schools__badge ${viewSchool.is_active ? 'schools__badge--active' : 'schools__badge--inactive'}`}>
                                                {viewSchool.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                {/* Leadership */}
                                <div className="schools__card">
                                    <h3 className="schools__card-title">Leadership</h3>
                                    <div className="schools__view-field">
                                        <span className="schools__view-label">Principal</span>
                                        <span className="schools__view-value">
                                            {viewSchool.principal
                                                ? `${viewSchool.principal.first_name}${viewSchool.principal.last_name ? ' ' + viewSchool.principal.last_name : ''}`
                                                : <span className="schools__muted">Unassigned</span>}
                                        </span>
                                    </div>
                                    {viewSchool.principal?.email && (
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">Principal Email</span>
                                            <span className="schools__view-value">{viewSchool.principal.email}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Contact & Location */}
                                <div className="schools__card">
                                    <h3 className="schools__card-title">Contact & Location</h3>
                                    <div className="schools__row">
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">Email</span>
                                            <span className="schools__view-value">{viewSchool.contact_email ?? <span className="schools__muted">—</span>}</span>
                                        </div>
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">Phone</span>
                                            <span className="schools__view-value">{viewSchool.contact_phone ?? <span className="schools__muted">—</span>}</span>
                                        </div>
                                    </div>
                                    <div className="schools__view-field">
                                        <span className="schools__view-label">Website</span>
                                        <span className="schools__view-value">{viewSchool.website ?? <span className="schools__muted">—</span>}</span>
                                    </div>
                                    <div className="schools__view-field">
                                        <span className="schools__view-label">Address</span>
                                        <span className="schools__view-value">{viewSchool.address ?? <span className="schools__muted">—</span>}</span>
                                    </div>
                                    <div className="schools__row">
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">City</span>
                                            <span className="schools__view-value">{viewSchool.city ?? <span className="schools__muted">—</span>}</span>
                                        </div>
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">State / Province</span>
                                            <span className="schools__view-value">{viewSchool.state ?? <span className="schools__muted">—</span>}</span>
                                        </div>
                                    </div>
                                    <div className="schools__row">
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">Postal Code</span>
                                            <span className="schools__view-value">{viewSchool.postal_code ?? <span className="schools__muted">—</span>}</span>
                                        </div>
                                        <div className="schools__view-field">
                                            <span className="schools__view-label">Country</span>
                                            <span className="schools__view-value">{viewSchool.country ?? <span className="schools__muted">—</span>}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Board Affiliations */}
                                <div className="schools__card">
                                    <h3 className="schools__card-title">Board Affiliations</h3>
                                    {viewSchool.boards && viewSchool.boards.length > 0 ? (
                                        <div className="schools__board-entries schools__board-entries--view">
                                            {viewSchool.boards.map((b) => (
                                                <div key={b.id} className="schools__board-entry">
                                                    <div className="schools__board-entry-header">
                                                        <span className="schools__board-entry-name">{b.name} ({b.code})</span>
                                                    </div>
                                                    <div className="schools__row">
                                                        <div className="schools__view-field">
                                                            <span className="schools__view-label">Affiliation Number</span>
                                                            <span className="schools__view-value">
                                                                {b.affiliation?.affiliation_number ?? <span className="schools__muted">—</span>}
                                                            </span>
                                                        </div>
                                                        <div className="schools__view-field">
                                                            <span className="schools__view-label">Affiliated Since</span>
                                                            <span className="schools__view-value">
                                                                {b.affiliation?.affiliated_since ?? <span className="schools__muted">—</span>}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="schools__view-field">
                                                        <span className="schools__view-label">Expires At</span>
                                                        <span className="schools__view-value">
                                                            {b.affiliation?.expires_at ?? <span className="schools__muted">—</span>}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="schools__muted">No boards affiliated</span>
                                    )}
                                </div>

                                {/* Timestamps */}
                                <div className="schools__timestamps">
                                    <span className="schools__timestamp">Created: {formatTimestamp(viewSchool.created_at)}</span>
                                    <span className="schools__timestamp">Updated: {formatTimestamp(viewSchool.updated_at)}</span>
                                </div>
                            </div>
                        ) : null}

                        {/* Footer with Edit button */}
                        {viewSchool && !viewLoading && (
                            <div className="schools__drawer-footer">
                                <button
                                    className="schools__submit"
                                    type="button"
                                    onClick={() => {
                                        const s = mapSchoolToUi(viewSchool);
                                        const detail = viewSchool;
                                        setDrawerMode(null);
                                        setViewSchool(null);
                                        openEdit(s, detail);
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    Edit School
                                </button>
                                <button
                                    className="schools__cancel"
                                    type="button"
                                    onClick={closeDrawer}
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </aside>
                </>
            )}

            {/* ============= Drawer (Create / Edit) ============= */}
            {(drawerMode === 'create' || drawerMode === 'edit') && (
                <>
                    <div className="schools__overlay" onClick={closeDrawer} />
                    <aside className="schools__drawer">
                        {/* Drawer header */}
                        <div className="schools__drawer-header">
                            <h2 className="schools__drawer-title">
                                {drawerMode === 'edit' ? 'Edit School' : 'Add School'}
                            </h2>
                            <button className="schools__drawer-close" onClick={closeDrawer} title="Close">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        {/* Drawer body */}
                        <form
                            className="schools__drawer-body"
                            onSubmit={handleSubmit}
                            noValidate
                            id="school-form"
                        >
                            {/* Card 1: Basic Info */}
                            <div className="schools__card">
                                <h3 className="schools__card-title">Basic Information</h3>

                                <div className="schools__field">
                                    <label className="schools__label schools__label--required" htmlFor="schoolName">
                                        School Name
                                    </label>
                                    <input
                                        className={`schools__input ${errors.name ? 'schools__input--error' : ''}`}
                                        type="text"
                                        id="schoolName"
                                        placeholder="e.g. Springfield Public School"
                                        value={formName}
                                        onChange={(e) => {
                                            setFormName(e.target.value);
                                            if (errors.name) setErrors((p) => ({ ...p, name: undefined }));
                                        }}
                                        maxLength={255}
                                        autoFocus
                                    />
                                    {errors.name && <p className="schools__error">{errors.name}</p>}
                                </div>

                                <div className="schools__row">
                                    <div className="schools__field">
                                        <label className="schools__label schools__label--required" htmlFor="schoolCode">
                                            School Code
                                        </label>
                                        <div className="schools__input-wrap">
                                            <input
                                                className={`schools__input ${errors.school_code ? 'schools__input--error' : ''}`}
                                                type="text"
                                                id="schoolCode"
                                                placeholder="e.g. SCH-001"
                                                value={formCode}
                                                onChange={(e) => {
                                                    setFormCode(e.target.value);
                                                    if (errors.school_code) setErrors((p) => ({ ...p, school_code: undefined }));
                                                }}
                                                maxLength={50}
                                            />
                                            <span
                                                className={`schools__input-status ${codeStatus === 'available'
                                                    ? 'schools__input-status--available'
                                                    : codeStatus === 'taken'
                                                        ? 'schools__input-status--taken'
                                                        : codeStatus === 'loading'
                                                            ? 'schools__input-status--loading'
                                                            : ''
                                                    }`}
                                            >
                                                {codeStatus === 'available' && '✓'}
                                                {codeStatus === 'taken' && '✗'}
                                                {codeStatus === 'loading' && (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                        <circle cx="12" cy="12" r="10" strokeWidth="3" stroke="currentColor" strokeDasharray="50" strokeDashoffset="35" strokeLinecap="round" />
                                                    </svg>
                                                )}
                                            </span>
                                        </div>
                                        {errors.school_code && <p className="schools__error">{errors.school_code}</p>}
                                    </div>

                                    <div className="schools__field">
                                        <label className="schools__label" htmlFor="estYear">
                                            Established Year
                                        </label>
                                        <input
                                            className="schools__input"
                                            type="number"
                                            id="estYear"
                                            placeholder="e.g. 2005"
                                            value={formEstYear}
                                            onChange={(e) => setFormEstYear(e.target.value)}
                                            min={1800}
                                            max={new Date().getFullYear()}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Leadership */}
                            <div className="schools__card">
                                <h3 className="schools__card-title">Leadership</h3>

                                <div className="schools__field">
                                    <label className="schools__label" htmlFor="principal">
                                        Principal
                                    </label>
                                    <select
                                        className="schools__input"
                                        id="principal"
                                        value={formPrincipalId ?? ''}
                                        onChange={(e) => handlePrincipalChange(e.target.value)}
                                        disabled={staffLoading}
                                    >
                                        <option value="">
                                            {staffLoading ? 'Loading staff…' : '— Select Principal —'}
                                        </option>
                                        {staffOptions.map((s) => (
                                            <option key={s.userId} value={s.userId}>
                                                {s.name}{s.designation ? ` — ${s.designation}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Card 3: Contact & Location */}
                            <div className="schools__card">
                                <h3 className="schools__card-title">Contact & Location</h3>

                                <div className="schools__row">
                                    <div className="schools__field">
                                        <label className="schools__label" htmlFor="schoolEmail">Email</label>
                                        <input className="schools__input" type="email" id="schoolEmail" placeholder="hello@school.edu" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} />
                                    </div>
                                    <div className="schools__field">
                                        <label className="schools__label" htmlFor="schoolPhone">Phone</label>
                                        <input className="schools__input" type="tel" id="schoolPhone" placeholder="+91 98765 43210" value={formPhone} onChange={(e) => setFormPhone(e.target.value)} maxLength={20} />
                                    </div>
                                </div>

                                <div className="schools__field">
                                    <label className="schools__label" htmlFor="schoolWebsite">Website</label>
                                    <input className="schools__input" type="url" id="schoolWebsite" placeholder="https://school.edu" value={formWebsite} onChange={(e) => setFormWebsite(e.target.value)} />
                                </div>

                                <div className="schools__field">
                                    <label className="schools__label" htmlFor="schoolAddress">Street Address</label>
                                    <textarea className="schools__input schools__textarea" id="schoolAddress" placeholder="Street address, building, floor…" value={formAddress} onChange={(e) => setFormAddress(e.target.value)} />
                                </div>

                                <div className="schools__row">
                                    <div className="schools__field">
                                        <label className="schools__label" htmlFor="schoolCity">City</label>
                                        <input className="schools__input" type="text" id="schoolCity" placeholder="City" value={formCity} onChange={(e) => setFormCity(e.target.value)} maxLength={100} />
                                    </div>
                                    <div className="schools__field">
                                        <label className="schools__label" htmlFor="schoolState">State / Province</label>
                                        <input className="schools__input" type="text" id="schoolState" placeholder="State" value={formState} onChange={(e) => setFormState(e.target.value)} maxLength={100} />
                                    </div>
                                </div>

                                <div className="schools__row">
                                    <div className="schools__field">
                                        <label className="schools__label" htmlFor="schoolPostal">Postal Code</label>
                                        <input className="schools__input" type="text" id="schoolPostal" placeholder="560001" value={formPostal} onChange={(e) => setFormPostal(e.target.value)} maxLength={20} />
                                    </div>
                                    <div className="schools__field">
                                        <label className="schools__label" htmlFor="schoolCountry">Country</label>
                                        <input className="schools__input" type="text" id="schoolCountry" placeholder="India" value={formCountry} onChange={(e) => setFormCountry(e.target.value)} maxLength={100} />
                                    </div>
                                </div>
                            </div>

                            {/* Card 4: Affiliations */}
                            <div className="schools__card">
                                <h3 className="schools__card-title">Board Affiliations</h3>
                                <div className="schools__board-options">
                                    {boards.map((b) => (
                                        <button
                                            key={b.id}
                                            type="button"
                                            className={`schools__board-option ${formBoards.some((fb) => fb.board_id === b.id) ? 'schools__board-option--selected' : ''}`}
                                            onClick={() => toggleBoard(b.id)}
                                        >
                                            {formBoards.some((fb) => fb.board_id === b.id) ? '✓ ' : '+ '}{b.name} ({b.code})
                                        </button>
                                    ))}
                                </div>

                                {formBoards.length > 0 && (
                                    <div className="schools__board-entries">
                                        {formBoards.map((entry) => {
                                            const board = boards.find((b) => b.id === entry.board_id);
                                            if (!board) return null;
                                            return (
                                                <div key={entry.board_id} className="schools__board-entry">
                                                    <div className="schools__board-entry-header">
                                                        <span className="schools__board-entry-name">{board.name} ({board.code})</span>
                                                        <button
                                                            type="button"
                                                            className="schools__tag-remove"
                                                            onClick={() => toggleBoard(entry.board_id)}
                                                            title="Remove board"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                    <div className="schools__field">
                                                        <label className="schools__label" htmlFor={`affNum-${entry.board_id}`}>
                                                            Affiliation Number
                                                        </label>
                                                        <input
                                                            className="schools__input"
                                                            type="text"
                                                            id={`affNum-${entry.board_id}`}
                                                            placeholder="e.g. CBSE/AFF/2430001"
                                                            value={entry.affiliation_number}
                                                            onChange={(e) => updateBoardField(entry.board_id, 'affiliation_number', e.target.value)}
                                                            maxLength={100}
                                                        />
                                                    </div>
                                                    <div className="schools__row">
                                                        <div className="schools__field">
                                                            <label className="schools__label" htmlFor={`affSince-${entry.board_id}`}>
                                                                Affiliated Since
                                                            </label>
                                                            <input
                                                                className="schools__input"
                                                                type="date"
                                                                id={`affSince-${entry.board_id}`}
                                                                value={entry.affiliated_since}
                                                                onChange={(e) => updateBoardField(entry.board_id, 'affiliated_since', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="schools__field">
                                                            <label className="schools__label" htmlFor={`affExpires-${entry.board_id}`}>
                                                                Expires At
                                                            </label>
                                                            <input
                                                                className={`schools__input ${entry.expires_at && entry.affiliated_since && entry.expires_at <= entry.affiliated_since ? 'schools__input--error' : ''}`}
                                                                type="date"
                                                                id={`affExpires-${entry.board_id}`}
                                                                value={entry.expires_at}
                                                                onChange={(e) => updateBoardField(entry.board_id, 'expires_at', e.target.value)}
                                                                min={entry.affiliated_since || undefined}
                                                            />
                                                            {entry.expires_at && entry.affiliated_since && entry.expires_at <= entry.affiliated_since && (
                                                                <p className="schools__error">Must be after affiliated since date</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Card 5: Settings */}
                            <div className="schools__card">
                                <h3 className="schools__card-title">Settings</h3>
                                <div className="schools__toggle-row">
                                    <div className="schools__toggle-info">
                                        <span className="schools__toggle-label">Active Status</span>
                                        <span className="schools__toggle-desc">
                                            Inactive schools are hidden from normal operations
                                        </span>
                                    </div>
                                    <label className="schools__toggle">
                                        <input
                                            type="checkbox"
                                            checked={formIsActive}
                                            onChange={(e) => setFormIsActive(e.target.checked)}
                                        />
                                        <span className="schools__toggle-track" />
                                    </label>
                                </div>
                            </div>

                            {/* Timestamps (edit only) */}
                            {drawerMode === 'edit' && editCreatedAt && (
                                <div className="schools__timestamps">
                                    <span className="schools__timestamp">Created: {formatTimestamp(editCreatedAt)}</span>
                                    <span className="schools__timestamp">Updated: {formatTimestamp(editUpdatedAt)}</span>
                                </div>
                            )}
                        </form>

                        {/* Drawer footer (sticky) */}
                        <div className="schools__drawer-footer">
                            <button
                                className="schools__submit"
                                type="submit"
                                form="school-form"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="schools__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" strokeWidth="3" />
                                        </svg>
                                        {drawerMode === 'edit' ? 'Updating…' : 'Creating…'}
                                    </>
                                ) : drawerMode === 'edit' ? (
                                    'Update School'
                                ) : (
                                    'Create School'
                                )}
                            </button>
                            <button
                                className="schools__cancel"
                                type="button"
                                onClick={closeDrawer}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </aside>
                </>
            )}

            {/* ============= Delete Confirmation Modal ============= */}
            {deleteTarget && (
                <div className="schools__confirm-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="schools__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="schools__confirm-title">Remove School</h3>
                        <p className="schools__confirm-text">
                            Are you sure you want to remove <strong>{deleteTarget.name}</strong>? This
                            action can be undone by an administrator.
                        </p>
                        <div className="schools__confirm-actions">
                            <button className="schools__confirm-cancel" onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </button>
                            <button className="schools__confirm-delete" onClick={confirmDelete}>
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
